import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { PdfIcon } from 'src/v3/core/icons'
import {
  selectUserDetailsModal,
  selectUserActivitiesForDetails,
  selectDownloadState,
  selectSelectedLinkedServiceID,
  setUserDetailsOpen,
} from '../../index'
import { selectUsersByLinkedService } from '../../../../manage.slice'

type Props = {
  onDownloadCertificate: (
    certificateId: string,
    activityType: string,
    fileName: string,
  ) => void
}

export default function UserDetailsModal({ onDownloadCertificate }: Props) {
  let dispatch = useAppDispatch()

  // Redux selectors
  let userDetailsModal = useAppSelector(selectUserDetailsModal)
  let userActivities = useAppSelector(selectUserActivitiesForDetails)
  let downloadState = useAppSelector(selectDownloadState)
  let selectedLinkedServiceID = useAppSelector(selectSelectedLinkedServiceID)
  let users = useAppSelector(
    selectUsersByLinkedService(selectedLinkedServiceID),
  )

  // Get user details
  let selectedUser = (users as any[])?.find(
    (user: any) => user.id === userDetailsModal.selectedUserForDetails,
  )

  let handleClose = () => {
    dispatch(setUserDetailsOpen(false))
  }

  let handleDownload = (activity: any) => {
    if (activity.certificateId) {
      let fileName =
        `${selectedUser?.firstName}_${selectedUser?.lastName}_${activity.courseTitle}`.replace(
          /[^a-zA-Z0-9]/g,
          '_',
        )
      onDownloadCertificate(
        activity.certificateId,
        activity.activityType,
        fileName,
      )
    }
  }

  if (!userDetailsModal.userDetailsOpen || !selectedUser) {
    return null
  }

  return (
    <Dialog
      open={userDetailsModal.userDetailsOpen}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: { bgcolor: 'background.paper' },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          <Typography color="text.primary" variant="h6">
            {selectedUser.firstName} {selectedUser.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedUser.primaryEmail}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper' }}>
        {/* User Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography
            color="text.primary"
            variant="subtitle2"
            sx={{
              mb: 1,
            }}
          >
            CE Activity Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`${userActivities.length} Activities`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${userActivities.reduce((sum, a) => sum + a.ceHours, 0)} Total Hours`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${new Set(userActivities.map((a) => a.category)).size} Categories`}
              color="info"
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Activities List */}
        <Typography
          color="text.primary"
          variant="subtitle2"
          sx={{
            mb: 2,
          }}
        >
          Detailed Activities
        </Typography>

        {userActivities.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 4 }}
          >
            No activities found for this user in the selected time range.
          </Typography>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {userActivities.map((activity, index) => (
              <ListItem
                key={activity.id || index}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {activity.courseTitle}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${activity.ceHours}h`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={activity.activityType}
                            color={
                              activity.activityType === 'recorded'
                                ? 'info'
                                : activity.activityType === 'live'
                                  ? 'warning'
                                  : 'default'
                            }
                            variant="filled"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Completed:</strong>{' '}
                            {dayjs(activity.completionDate).format(
                              'MMM DD, YYYY',
                            )}{' '}
                            ({dayjs(activity.completionDate).fromNow()})
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Category:</strong> {activity.category}
                          </Typography>
                          {activity.courseCode && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              <strong>Course Code:</strong>{' '}
                              {activity.courseCode}
                            </Typography>
                          )}
                          {activity.licenseTypes &&
                            activity.licenseTypes.length > 0 && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>License Types:</strong>{' '}
                                {activity.licenseTypes.join(', ')}
                              </Typography>
                            )}
                          {activity.wasAssigned && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                size="small"
                                label={`Assignment: ${activity.assignmentName}`}
                                color="secondary"
                                variant="outlined"
                              />
                              {activity.assignmentDueDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  Due:{' '}
                                  {dayjs(activity.assignmentDueDate).format(
                                    'MMM DD, YYYY',
                                  )}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </Box>

                  {/* Certificate Download */}
                  {activity.certificateId && (
                    <Box sx={{ ml: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          downloadState.downloading ===
                          activity.certificateId ? (
                            <div>⏳</div>
                          ) : (
                            <PdfIcon />
                          )
                        }
                        onClick={() => handleDownload(activity)}
                        disabled={
                          downloadState.downloading === activity.certificateId
                        }
                        sx={{ minWidth: 'auto' }}
                      >
                        {downloadState.downloading === activity.certificateId
                          ? 'Downloading...'
                          : 'Certificate'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
